/** Only ever loaded after explicit opt-in consent (see CookieConsentContext.tsx) - never baked
 *  into index.html directly, unlike the cookieless Umami tracker (see ANALYTICS.md), because
 *  Google Analytics sets cookies and requires prior consent under Art. 6(1)(a) DSGVO / § 25 TTDSG
 *  (see content/datenschutzerklaerung.ts, section 11). */
const GA_MEASUREMENT_ID = 'G-3N7450T18R'
const SCRIPT_ID = 'ga-gtag-script'

declare global {
  interface Window {
    dataLayer?: unknown[]
    [key: `ga-disable-${string}`]: boolean | undefined
  }
}

function gtag(...args: unknown[]) {
  window.dataLayer!.push(args)
}

/** Idempotent - safe to call on every app start once consent is already stored, without risking a
 *  duplicate script tag/double init if called more than once in the same session. */
export function loadGoogleAnalytics(): void {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false
  if (document.getElementById(SCRIPT_ID)) return

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID)
}

/** Called when consent is declined or later revoked via "Cookie-Einstellungen" (see
 *  LegalLinks.tsx). `ga-disable-<id>` is Google's own documented kill switch - gtag.js checks it
 *  before sending anything, so this also covers the case where the script was already loaded
 *  earlier in the session. Also removes the script tag and any already-set GA cookies for a clean
 *  revoke, best-effort (can't recall data already sent before the revoke). */
export function disableGoogleAnalytics(): void {
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = true
  document.getElementById(SCRIPT_ID)?.remove()

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim()
    if (name?.startsWith('_ga')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
  }
}
